import { useApiMutation, type ApiClient } from "@gladis/api-client";
import { EXPENSE_CATEGORIES, formatEur } from "@gladis/shared";
import {
  Button,
  Dialog,
  Label,
  MoneyInput,
  parseEurInput,
  SelectField,
  TextField,
} from "@gladis/ui";
import { expenseCategoryLabel, errorSentence, tRich, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { EXPENSES_KEY, type ExpenseRowDto } from "./ExpenseList.js";
import { INTERVAL_OPTIONS } from "./intervals.js";
import { SplitEditor, splitsAreSubmittable, type EditableSplit } from "./SplitEditor.js";

/**
 * Add a recurring expense (FR-001).
 *
 * THE CATEGORY OPTIONS COME FROM THE CATALOGUE, and that is the whole point of the catalogue. There is no
 * list of seventeen names in this file, no label map, and no ordering decision — adding an eighteenth
 * category makes it appear here with no edit at all. An architecture test fails if a second copy of the
 * list turns up anywhere.
 *
 * THE MONTHLY PREVIEW IS COMPUTED HERE, and it is the one figure in the feature that is. It is a hint while
 * the member types, not a stored or displayed total: the row's real figure arrives from the server after the
 * write, computed by the same single rounding rule as every other number on the page. Two implementations of
 * an ARITHMETIC RULE would be a bug; two implementations of a preview is a preview.
 *
 * IT IS A MODAL, for the reason `CardForm` is and one more of its own. The shared reason: an inline panel
 * pushed the settle-up, the filters and the whole list down by its own height the moment it opened. The
 * extra one: this is the tallest form in the application — seven fields and a split editor — so it moved
 * more of the page than any other, and `Dialog` is the only place in the app where a form that outgrows
 * the viewport still has a reachable submit button.
 *
 * ITS HEADING AND SENTENCE ARE `Dialog` PROPS. The frame and the `<h2>` this file used to draw are the
 * dialog's now; the words are still expenses'.
 *
 * ==========================================================================================
 * IT IS ALSO THE VEHICLES MODULE'S COST FORM, VIA `destination`
 * ==========================================================================================
 *
 * A vehicle's recurring costs ARE recurring expenses, so the form for adding one is this form. The
 * only thing that differs is where the body is posted: `/vehicles/:id/costs` creates the expense and
 * links it in a single transaction, where `/expenses` just creates it.
 *
 * That is the whole of the prop, and it is deliberately the *only* seam. A second form in the
 * vehicles package existed for a few hours and had already drifted: no card picker, no split editor,
 * and a monthly preview that a guard refused. Every one of those is a field this form gets right,
 * and each would have had to be re-added and re-argued.
 *
 * **This component learns nothing about vehicles.** It is handed a function; the knowledge that a
 * vehicle has costs lives in `apps/app`, which is the one place two contexts may meet. The
 * dependency rules are unmoved — `@gladis/domain-expenses` imports no vehicle type and never sees
 * one.
 */

/**
 * Where a NEW expense goes, when it is not simply an expense.
 *
 * EDITING IS NOT OVERRIDABLE and there is no case for it: an edit is `PATCH /api/expenses/:id`
 * wherever the form was opened from, because the row being changed is an expense and the link that
 * points at it is not being touched.
 */
export interface ExpenseDestination {
  /** Posts the assembled body. The vehicle route wraps it as `{ expense: body }`. */
  post: (client: ApiClient, body: Record<string, unknown>) => Promise<{ id: string }>;
  /** Caches to drop beyond the expenses list — the vehicle's own page reads the same rows. */
  invalidates?: readonly (readonly unknown[])[];
  /** Replaces the submit button's label, so "Add expense" can become "Add this cost". */
  submitLabel?: string;
  /**
   * Replaces the dialog's heading, so it can name what the cost is FOR.
   *
   * "Add an expense" on a page whose whole subject is one car is technically true and reads as if
   * the member has wandered off the page they were on. "Add a cost for Damien's Golf" is the
   * sentence that says the link is going to be made — which is the only thing this form does
   * differently here, and the only place it is visible before submitting.
   */
  heading?: string;
}

export function ExpenseForm({
  open,
  onClose,
  members = [],
  cards = [],
  editing,
  onCreated,
  destination,
  currentAnchor = null,
}: {
  open: boolean;
  onClose: () => void;
  /** Current active members, for the split editor. Empty means no split can be entered. */
  members?: readonly { userId: string; displayName: string }[];
  /** The household's cards. The picker offers the ACTIVE ones only (FR-044). */
  cards?: readonly {
    id: string;
    name: string;
    identifier: string | null;
    archivedAt: string | null;
  }[];
  /**
   * The expense being changed, or null to record a new one.
   *
   * ONE FORM FOR BOTH, and not for economy: every rule this form enforces — the amount's two decimals, the
   * six intervals, the forty-year horizon on a start date, a split totalling exactly 100% with its frozen
   * shares read-only — applies identically to an edit. A second form would be a second copy of all of it,
   * and the copy would be the one that drifted. `PATCH /api/expenses/:id` shipped with the feature and had
   * no caller; this is it.
   */
  editing?: ExpenseRowDto | null;
  onCreated?: () => void;
  /** Sends a NEW expense somewhere other than `/expenses`. See `ExpenseDestination`. */
  destination?: ExpenseDestination;
  /**
   * The first payment date in force today for the expense being edited (feature 024), from the schedule
   * read's `currentAnchor`. Not on `ExpenseRowDto` on purpose: the page read's query budget must not move.
   */
  currentAnchor?: string | null;
}): ReactNode {
  const t = useT();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState<string>("monthly");
  const [category, setCategory] = useState<string>("housing");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [splits, setSplits] = useState<EditableSplit[]>([]);
  const [cardId, setCardId] = useState("");
  // Feature 024. "" is undated — the field may be left empty (FR-005).
  const [firstPaymentDate, setFirstPaymentDate] = useState("");
  /*
   * THE ANCHOR THE FORM WAS FILLED WITH, and what "changed" and "required" are measured against. The live prop
   * can arrive after the dialog opened — the schedule read is still loading on a page that has just mounted —
   * and measured against it, an untouched empty field would read as a CLEAR and erase the date on save.
   */
  const [anchorAtOpen, setAnchorAtOpen] = useState<string | null>(null);

  /*
   * THE FIELDS ARE FILLED FROM THE ROW WHEN THE DIALOG OPENS, keyed on which expense it opened for.
   *
   * Keyed on the id rather than on `open` alone: opening the form on one expense, closing it, and opening it
   * on another must not leave the first one's values behind — and re-running on every render would fight the
   * member's typing.
   */
  const openedFor = editing?.id ?? null;
  const [filledFor, setFilledFor] = useState<string | null | undefined>(undefined);

  /*
   * THREE STATES, AND THE THIRD IS WHAT MAKES REOPENING WORK: `undefined` while closed, `null` while
   * creating, an id while editing.
   *
   * With only `id | null`, closing the dialog and reopening it on the SAME expense left `filledFor` equal to
   * `openedFor`, so the fields were never re-filled — a member who edited an amount, cancelled and opened it
   * again saw their abandoned edit, and one who saved and reopened saw the blanked fields the save had left
   * behind. That second case sent an empty description and the server refused it, which is how it was found.
   *
   * `undefined` rather than a `"closed"` string, because a string literal in a union with `string` is not a
   * distinct state at all — the linter says so, and it is right: `null` and `undefined` are the two values a
   * member's expense id can never be.
   */
  if (!open) {
    if (filledFor !== undefined) setFilledFor(undefined);
  } else if (openedFor !== filledFor) {
    setFilledFor(openedFor);
    if (editing) {
      setDescription(editing.description);
      setAmount(editing.amountInEuros.toFixed(2));
      setInterval(editing.interval);
      setCategory(editing.category);
      setStartDate(editing.startDate);
      setCardId(editing.card?.id ?? "");
      setFirstPaymentDate(currentAnchor ?? "");
      setAnchorAtOpen(currentAnchor);
      setSplits(
        editing.splits.map((split) => ({
          memberId: split.memberId ?? "",
          memberDisplayName: split.memberDisplayName,
          percentage: split.percentage,
          isFrozen: split.isFrozen,
        })),
      );
    } else {
      setDescription("");
      setAmount("");
      setInterval("monthly");
      setCategory("housing");
      setStartDate(new Date().toISOString().slice(0, 10));
      setCardId("");
      setFirstPaymentDate("");
      setAnchorAtOpen(null);
      setSplits([]);
    }
  }

  /*
   * FR-006: a date stated for one interval says nothing about another, so changing the interval of a DATED
   * expense clears the field (below, in the interval's `onChange`) and makes it required. An undated expense
   * may change interval and stay undated. The server enforces the same rule and says the same sentence.
   */
  const intervalChanged =
    editing !== null && editing !== undefined && editing.interval !== interval;
  const paymentDateRequired = intervalChanged && anchorAtOpen !== null;

  const create = useApiMutation<{ id: string }, Record<string, unknown>>(
    (client, input) => {
      if (editing) return client.patch<{ id: string }>(`/expenses/${editing.id}`, input);
      // The destination owns the whole request, body shape included — see `ExpenseDestination`.
      return destination
        ? destination.post(client, input)
        : client.post<{ id: string }>("/expenses", input);
    },
    { invalidates: [EXPENSES_KEY, ...(destination?.invalidates ?? [])] },
  );

  const euros = parseEurInput(amount);
  const ratio = INTERVAL_OPTIONS.find((entry) => entry.value === interval);
  /*
   * ACTIVE cards only (FR-044).
   *
   * An archived card is not offered — but an expense already bound to one keeps displaying it, which is why the
   * list's row reads the card from the payload rather than from this list.
   */
  const activeCards = cards.filter((card) => card.archivedAt === null);

  /*
   * A PREVIEW, in floating point, and that is acceptable here for a reason worth stating.
   *
   * Every stored and displayed figure in this feature is computed in integer cents by
   * `Valuation.scaledBy`. This is neither: it is a hint that disappears the moment the member submits, and
   * it is never summed into anything. Reaching for the domain's arithmetic would mean importing the domain
   * root into a `/ui` component — which is permitted — to compute a number that is thrown away.
   *
   * It rounds the same way (half away from zero on a positive value), so the preview and the row agree.
   */
  const monthlyPreview =
    euros === null || !ratio
      ? null
      : Math.round((euros * ratio.numerator * 100) / ratio.denominator) / 100;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (euros === null) return;

    create.mutate(
      {
        description,
        amount: euros,
        interval,
        category,
        startDate,
        /*
         * ON A CREATE, an empty card or split is OMITTED — the server reads an absent field as "none" and
         * never has to interpret an empty array.
         *
         * ON AN EDIT, both are sent EXPLICITLY, and that is the whole difference between the two requests.
         * `PATCH` treats an omitted field as "leave it alone", so omitting the card is how a member says
         * "don't touch it" — which means clearing one has to be `null` and clearing a split has to be `[]`.
         * Omit them on an edit and the form silently cannot remove a card or make a cost the household's.
         */
        ...(editing ? { cardId: cardId === "" ? null : cardId } : cardId === "" ? {} : { cardId }),
        /*
         * THE DUE DATE (024). A create sends it only when given. An edit sends it only when it CHANGED from
         * what is in force — absent is "leave it", and a repeated date would write a row saying nothing — and
         * always after an interval change, where the server requires an answer and `null` is one.
         */
        ...(editing
          ? intervalChanged || firstPaymentDate !== (anchorAtOpen ?? "")
            ? { firstPaymentDate: firstPaymentDate === "" ? null : firstPaymentDate }
            : {}
          : firstPaymentDate === ""
            ? {}
            : { firstPaymentDate }),
        ...(editing || splits.length > 0
          ? {
              splits: splits.map((split) => ({
                memberId: split.memberId,
                percentage: split.percentage,
              })),
            }
          : {}),
      },
      {
        onSuccess: () => {
          /*
           * CLEARED ONLY ON A CREATE. The form stays open for the next expense in that case, so leaving the
           * previous one's description in it would be a trap; an edit closes, and the fields are re-filled
           * from the row the next time it opens.
           */
          if (!editing) {
            setDescription("");
            setAmount("");
            setSplits([]);
            setCardId("");
            setFirstPaymentDate("");
          }
          onCreated?.();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t("exp.list.edit") : (destination?.heading ?? t("exp.form.addHeading"))}
      description={editing ? t("exp.form.editHint") : t("exp.form.hint")}
    >
      <form onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="expense-description">{t("exp.form.descriptionLabel")}</Label>
            <TextField
              id="expense-description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
              placeholder={t("exp.form.descriptionPlaceholder")}
              required
            />
          </div>

          <div>
            <Label htmlFor="expense-amount">{t("exp.form.amount")}</Label>
            <MoneyInput
              id="expense-amount"
              value={amount}
              onValueChange={(next) => {
                setAmount(next);
              }}
              required
            />
          </div>

          <div>
            <Label htmlFor="expense-interval">{t("exp.form.howOften")}</Label>
            <SelectField
              id="expense-interval"
              value={interval}
              onChange={(event) => {
                setInterval(event.target.value);
                // Never carried to a different interval; restored if the member changes their mind.
                if (editing && event.target.value !== editing.interval) setFirstPaymentDate("");
                else if (editing) setFirstPaymentDate(anchorAtOpen ?? "");
              }}
            >
              {INTERVAL_OPTIONS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {t(entry.labelKey)}
                </option>
              ))}
            </SelectField>
          </div>

          <div>
            <Label htmlFor="expense-category">{t("exp.form.category")}</Label>
            <SelectField
              id="expense-category"
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
              }}
            >
              {/* Derived from the catalogue. No list of names lives in this file. */}
              {EXPENSE_CATEGORIES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {expenseCategoryLabel(entry.value)}
                </option>
              ))}
            </SelectField>
          </div>

          <div>
            <Label htmlFor="expense-start">{t("exp.form.startedOn")}</Label>
            <TextField
              id="expense-start"
              type="date"
              value={startDate}
              onChange={(event) => {
                setStartDate(event.target.value);
              }}
              required
            />
          </div>

          <div>
            <Label htmlFor="expense-first-payment">{t("exp.form.paymentDate")}</Label>
            <TextField
              id="expense-first-payment"
              type="date"
              value={firstPaymentDate}
              onChange={(event) => {
                setFirstPaymentDate(event.target.value);
              }}
              required={paymentDateRequired}
              aria-describedby="expense-first-payment-hint"
            />
            <p id="expense-first-payment-hint" className="mt-1 text-xs text-muted">
              {paymentDateRequired
                ? t("exp.form.paymentDateRequiredOnIntervalChange")
                : t("exp.form.paymentDateHint")}
            </p>
          </div>
        </div>

        {activeCards.length > 0 ? (
          <div className="mt-4">
            <Label htmlFor="expense-card">{t("exp.form.paidFrom")}</Label>
            <SelectField
              id="expense-card"
              value={cardId}
              onChange={(event) => {
                setCardId(event.target.value);
              }}
            >
              {/* Unattributed is the default and a legitimate answer, not a missing one. */}
              <option value="">{t("exp.form.notAttributed")}</option>
              {activeCards.map((card) => (
                <option key={card.id} value={card.id}>
                  {card.identifier === null ? card.name : `${card.name} — ${card.identifier}`}
                </option>
              ))}
            </SelectField>
            <p className="mt-1 text-xs text-muted">{t("exp.form.paidFromHint")}</p>
          </div>
        ) : null}

        {members.length > 0 ? (
          <div className="mt-4">
            <SplitEditor value={splits} members={members} onChange={setSplits} />
          </div>
        ) : null}

        {monthlyPreview === null ? null : (
          <p className="mt-4 text-sm text-muted" aria-live="polite">
            {tRich("exp.form.thatIsPerMonth", {
              amount: <span className="font-semibold text-ink">{formatEur(monthlyPreview)}</span>,
            })}
          </p>
        )}

        {create.error ? (
          <p className="mt-4 text-sm text-danger" role="alert">
            {errorSentence(create.error)}
          </p>
        ) : null}

        {/* Cancel beside Submit — see the note in `CardForm`: the three other ways out of a dialog are
            all invisible. */}
        <div className="mt-5 flex justify-end gap-3">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={create.isPending || euros === null || !splitsAreSubmittable(splits)}
          >
            {/*
              THE LABEL SAYS WHICH OPERATION THIS IS. "Add expense" on a form that is editing one is the
              kind of thing a member reads once, mistrusts, and then re-checks the list to be sure — and the
              e2e finds the button by its name, so the two modes have to be distinguishable to a test as well
              as to a person.
            */}
            {create.isPending
              ? editing
                ? t("common.saving")
                : t("exp.form.adding")
              : editing
                ? t("exp.form.saveChanges")
                : /*
                   * A DESTINATION MAY RENAME THIS, and the argument above is why it should. On a
                   * vehicle's page "Add expense" is the wrong noun for what the member is doing —
                   * they are adding a cost to a car — and the same paragraph applies: the label has
                   * to say which operation this is, to a person and to a locator.
                   */
                  (destination?.submitLabel ?? t("exp.form.add"))}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
